import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall, getAuthHeaders } from '../utils/customerUtils'

export interface Partner {
  id: number
  first_name: string
  last_name: string
  name: string
  email: string
  phone: string
  agency?: string
  commission_cut?: number
  created_at: string
  updated_at: string
}

interface PartnerFormData {
  first_name: string
  last_name: string
  email: string
  phone: string
  agency?: string
  commission_cut?: string
}

interface ValidationError {
  field: string
  message: string
}

interface PartnerFormProps {
  partner?: Partner
  onSubmit?: (partner: Partner) => void
  onCancel?: () => void
  isModal?: boolean
}

export function PartnerForm({ partner, onSubmit, onCancel, isModal = false }: PartnerFormProps) {
  const { t } = useTranslation()
  
  const [formData, setFormData] = useState<PartnerFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    agency: '',
    commission_cut: ''
  })
  
  const [errors, setErrors] = useState<ValidationError[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const isEditing = !!partner

  useEffect(() => {
    if (partner) {
      setFormData({
        first_name: partner.first_name || '',
        last_name: partner.last_name || '',
        email: partner.email || '',
        phone: partner.phone || '',
        agency: partner.agency || '',
        commission_cut: partner.commission_cut?.toString() || ''
      })
    }
  }, [partner])

  useEffect(() => {
    // Clear success message after 3 seconds
    if (submitSuccess) {
      const timer = setTimeout(() => setSubmitSuccess(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [submitSuccess])

  const handleInputChange = (field: keyof PartnerFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear field-specific errors
    setErrors(prev => prev.filter(error => error.field !== field))
    setSubmitError(null)
  }

  const getFieldError = (field: string): string | undefined => {
    return errors.find(error => error.field === field)?.message
  }

  const validateForm = (): ValidationError[] => {
    const errors: ValidationError[] = []

    if (!formData.first_name.trim()) {
      errors.push({ field: 'first_name', message: 'First name is required' })
    }

    if (!formData.last_name.trim()) {
      errors.push({ field: 'last_name', message: 'Last name is required' })
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push({ field: 'email', message: 'Please enter a valid email address' })
    }

    if (formData.commission_cut) {
      const cut = parseFloat(formData.commission_cut)
      if (isNaN(cut) || cut < 0 || cut > 100) {
        errors.push({ field: 'commission_cut', message: 'Commission cut must be between 0 and 100' })
      }
    }

    return errors
  }

  // Sanitize phone number - remove all non-numeric characters
  const sanitizePhone = (phone: string): string => {
    return phone.replace(/\D/g, '')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form
    const validationErrors = validateForm()
    
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setErrors([])

    try {
      const url = isEditing ? `api/partners/${partner.id}` : 'api/partners'
      const method = isEditing ? 'PUT' : 'POST'

      const submitData: any = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone ? sanitizePhone(formData.phone) : null,
        agency: formData.agency?.trim() || null,
        commission_cut: formData.commission_cut ? parseFloat(formData.commission_cut) : null
      }

      const response = await makeApiCall(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(submitData)
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 409) {
          setErrors([{ field: 'email', message: 'A partner with this email already exists' }])
          return
        }
        throw new Error(data.message || `Failed to ${isEditing ? 'update' : 'create'} partner`)
      }

      setSubmitSuccess(true)

      if (onSubmit) {
        onSubmit(data.partner)
      }

    } catch (error) {
      console.error('Error submitting partner:', error)
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`max-w-4xl mx-auto ${isModal ? 'w-full' : ''}`}>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Partner Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            {t('partners.form.partnerInformation')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('partners.form.firstName')} <span className="text-red-500">{t('partners.form.required')}</span>
              </label>
              <input
                type="text"
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  getFieldError('first_name') ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('partners.form.enterFirstName')}
                required
              />
              {getFieldError('first_name') && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{getFieldError('first_name')}</p>
              )}
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('partners.form.lastName')} <span className="text-red-500">{t('partners.form.required')}</span>
              </label>
              <input
                type="text"
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  getFieldError('last_name') ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('partners.form.enterLastName')}
                required
              />
              {getFieldError('last_name') && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{getFieldError('last_name')}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('partners.form.emailAddress')} <span className="text-gray-500 text-xs">{t('partners.form.optional')}</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  getFieldError('email') ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('partners.form.enterEmail')}
              />
              {getFieldError('email') && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{getFieldError('email')}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('partners.form.phoneNumber')} <span className="text-gray-500 text-xs">{t('partners.form.optional')}</span>
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
                placeholder={t('partners.form.enterPhone')}
              />
            </div>

            <div>
              <label htmlFor="agency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('partners.form.agency')} <span className="text-gray-500 text-xs">{t('partners.form.optional')}</span>
              </label>
              <input
                type="text"
                id="agency"
                value={formData.agency || ''}
                onChange={(e) => handleInputChange('agency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
                placeholder={t('partners.form.enterAgency')}
              />
            </div>

            <div>
              <label htmlFor="commission_cut" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('partners.form.commissionCut')} <span className="text-gray-500 text-xs">{t('partners.form.optional')}</span>
              </label>
              <input
                type="number"
                id="commission_cut"
                value={formData.commission_cut || ''}
                onChange={(e) => handleInputChange('commission_cut', e.target.value)}
                min="0"
                max="100"
                step="0.01"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  getFieldError('commission_cut') ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('partners.form.enterCommissionCut')}
              />
              {getFieldError('commission_cut') && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{getFieldError('commission_cut')}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('partners.form.commissionCutHint')}</p>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex items-center justify-end space-x-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
            >
              {t('partners.form.cancel')}
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting 
              ? t('partners.form.saving') 
              : isEditing 
                ? t('partners.form.updatePartner') 
                : t('partners.form.createPartner')
            }
          </button>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {isEditing ? t('partners.form.partnerUpdatedSuccess') : t('partners.form.partnerCreatedSuccess')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {submitError && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  {submitError}
                </p>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  )
}



